import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  message,
  Descriptions,
  Divider,
  Empty,
  Tabs,
  Progress, DatePicker, Input
} from 'antd';
import {
  ArrowLeftOutlined,
  ExportOutlined,
  EditOutlined,
  CheckOutlined,
  MailOutlined,
  DownloadOutlined,
  ReloadOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { useParams, useNavigate } from 'react-router-dom';
import { renderBillTypeTag, getBillTypeLabel } from '../../../utils/billTypeConfig';
import { getBillStatusColor, getBillStatusLabel } from '../../../utils/billI18n';
import { GET_BILL_DETAIL, GET_BILL_STATUS_TRANSITION_RECORDS } from '../../../graphql/queries/settlement/getSettlementBillQueries';
import {
  CONFIRM_BILL_INTERNAL,
  CONFIRM_BILL_ON_BEHALF_EXTERNAL,
  CREATE_ADJUSTMENT_TASK,
  EXPORT_BILL,
  CANCEL_BILL_SUMMARY,
  REJECT_BILL_ON_BEHALF_EXTERNAL,
} from '../../../graphql/mutations/settlement/settlementBillMutations';
import { PREVIEW_BILL_SEND } from '../../../graphql/queries/settlement/billSendQueries';
import { SEND_BILL } from '../../../graphql/mutations/settlement/billSendMutations';
import { GET_EXPORT_HISTORY, GET_EXPORT_FILE_DOWNLOAD_URL } from '../../../graphql/queries/settlement/billExportQueries';
import { GET_BILL_SUMMARY_HISTORY } from '../../../graphql/queries/settlement/billSummaryQueries';
import { GET_BILL_SEND_HISTORY } from '../../../graphql/queries/settlement/billSendQueries';
import { BillExportModal, type ExportLanguage } from '../../../components/settlement/BillExportModal';
import BillExportProgressModal from '../../../components/settlement/BillExportProgressModal';
import BillSummaryProgressModal from '../../../components/settlement/BillSummaryProgressModal';
import BillSendModal from '../../../components/settlement/BillSendModal';
import AdjustmentForm from '../../../components/settlement/AdjustmentForm';
import AdjustmentRecordList from '../../../components/settlement/AdjustmentRecordList';
import BillingPeriodRuleCard from '../../../components/settlement/BillingPeriodRuleCard';
import { downloadBillExportFile, formatFileSize, formatExportStatus } from '../../../utils/billExportDownload';
import { formatFileType, getFileTypeColor } from '../../../utils/fileTypeFormatter';
import { formatTimeWithTimezone } from '../../../utils/timezoneFormatter';
import { canExportBill, getExportButtonTitle, canAdjustBill, canViewInvoice, canSendBill, getSendBillButtonTitle } from '../../../utils/billExportPermission';
import { formatSettlementAmountFromCents, formatSettlementAmountFromMajor } from '../../../utils/settlementAmountFormatter';
import ClientDetailAdjustmentModal from '../../../components/settlement/ClientDetailAdjustmentModal';
import dayjs from 'dayjs';
import { BillTypeEnum } from '../../../graphql/types/settlement/CommonTypes';
import type {
  SettlementBillDetailResponseType,
  BillFeeSummaryType,
  AdjustmentRecordType,
  BillExportTemplateType,
  BillSendPreviewType
} from '../../../graphql/types/settlement';
import type { BillExportHistoryType } from '../../../graphql/types/settlement/BillExportTypes';
import type { BillSendRecordType } from '../../../graphql/types/settlement/BillSendTypes';
import type { BillSummaryHistory, BillStatusTransitionRecord } from '../../../graphql/types/settlement/settlementBillTypes';
import { useTranslation } from 'react-i18next';
import { HasResource } from '../../../components/HasResource';
import { ResourceCode } from '../../../constants/resourceCodes';
import { GET_BILL_INVOICES_BY_BILL_ID } from '../../../graphql/queries/settlement/billInvoiceQueries';
import { GET_BILL_RECEIPTS_BY_BILL_ID } from '../../../graphql/queries/settlement/billReceiptQueries';
import { CREATE_BILL_INVOICE, CANCEL_BILL_INVOICE } from '../../../graphql/mutations/settlement/billInvoiceMutations';
import { CREATE_BILL_RECEIPT, REVOKE_BILL_RECEIPT } from '../../../graphql/mutations/settlement/billReceiptMutations';
import {hasResource} from "../../../utils/resourceAuth";
import InvoiceSendRecordsTab from '../../../components/settlement/InvoiceSendRecordsTab';
import {
  OnBehalfExternalConfirmModal,
  OnBehalfExternalRejectModal,
} from '../../../components/settlement/RevenueBillOnBehalfModals';

/**
 * 账单详情页面
 * 基于 billing_service_frontend_scheduler_plan.md 方案实现
 */
const SettlementBillDetail: React.FC = () => {
  const { t } = useTranslation();
  const canRevenueBillOperate = hasResource([ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_OPERATE]);
  const { billId } = useParams<{ billId: string }>();
  const navigate = useNavigate();
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [progressModalVisible, setProgressModalVisible] = useState(false);
  const [exportTaskId, setExportTaskId] = useState<string | null>(null);
  const [summaryProgressModalVisible, setSummaryProgressModalVisible] = useState(false);
  const [summaryTaskId, setSummaryTaskId] = useState<string | null>(null);
  const [activeTabKey, setActiveTabKey] = useState<string>('billStatusTransition');
  const [detailAdjustmentModalVisible, setDetailAdjustmentModalVisible] = useState(false);
  const [onBehalfConfirmVisible, setOnBehalfConfirmVisible] = useState(false);
  const [onBehalfRejectVisible, setOnBehalfRejectVisible] = useState(false);
  const [onBehalfSubmitting, setOnBehalfSubmitting] = useState(false);

  // 发送账单（两步确认）
  const [billSendModalVisible, setBillSendModalVisible] = useState(false);
  const [billSendPreview, setBillSendPreview] = useState<BillSendPreviewType | null>(null);
  const [billSendSubmitting, setBillSendSubmitting] = useState(false);

  // GraphQL 查询
  const { data, loading, error, refetch } = useQuery(GET_BILL_DETAIL, {
    variables: { billId },
    fetchPolicy: 'cache-and-network',
    onError: (error) => {
      console.error('GraphQL Error:', error);
      message.error('查询账单详情失败: ' + error.message);
    }
  });

  // 导出历史查询（懒加载）
  const [loadExportHistory, { data: exportHistoryData, loading: exportHistoryLoading }] = useLazyQuery(
    GET_EXPORT_HISTORY,
    {
      variables: {
        billId: billId,
        clientId: null,
        pageRequest: { page: 1, pageSize: 20 }
      },
      fetchPolicy: 'network-only'
    }
  );

  // 获取导出文件下载URL（懒加载）
  const [getExportFileDownloadUrl] = useLazyQuery(GET_EXPORT_FILE_DOWNLOAD_URL, {
    fetchPolicy: 'network-only'
  });

  // 汇总历史查询（懒加载）
  const [loadSummaryHistory, { data: summaryHistoryData, loading: summaryHistoryLoading }] = useLazyQuery(
    GET_BILL_SUMMARY_HISTORY,
    {
      variables: { billId: billId },
      fetchPolicy: 'network-only'
    }
  );

  // 发送记录查询（懒加载）
  const [loadSendHistory, { data: sendHistoryData, loading: sendHistoryLoading }] = useLazyQuery(
    GET_BILL_SEND_HISTORY,
    {
      variables: {
        billId,
        pageRequest: { page: 1, size: 50 }
      },
      fetchPolicy: 'network-only'
    }
  );

  // 发送账单：预览（Step1）
  const [loadBillSendPreview] = useLazyQuery(PREVIEW_BILL_SEND, {
    fetchPolicy: 'network-only'
  });

  const [loadBillStatusTransitionRecords, { data: billStatusTransitionData, loading: billStatusTransitionLoading }] = useLazyQuery(
    GET_BILL_STATUS_TRANSITION_RECORDS,
    {
      variables: { billId },
      fetchPolicy: 'network-only'
    }
  );

  React.useEffect(() => {
    if (billId) {
      loadBillStatusTransitionRecords();
    }
  }, [billId, loadBillStatusTransitionRecords]);

  // GraphQL 变更
  const [confirmBill] = useMutation(CONFIRM_BILL_INTERNAL);
  const [confirmBillOnBehalfExternal] = useMutation(CONFIRM_BILL_ON_BEHALF_EXTERNAL);
  const [rejectBillOnBehalfExternal] = useMutation(REJECT_BILL_ON_BEHALF_EXTERNAL);
  const [sendBillMutation] = useMutation(SEND_BILL);
  const [createAdjustmentTask] = useMutation(CREATE_ADJUSTMENT_TASK);
  const [exportBill] = useMutation(EXPORT_BILL);
  const [cancelBillSummary] = useMutation(CANCEL_BILL_SUMMARY);

  // 开票相关变更
  const [createBillInvoice] = useMutation(CREATE_BILL_INVOICE);
  const [cancelBillInvoice] = useMutation(CANCEL_BILL_INVOICE);
  // 收款相关变更
  const [createBillReceipt] = useMutation(CREATE_BILL_RECEIPT);
  const [revokeBillReceipt] = useMutation(REVOKE_BILL_RECEIPT);
  // 开票相关状态
  const [invoiceAmount, setInvoiceAmount] = useState<string>('');
  const [invoiceDate, setInvoiceDate] = useState<dayjs.Dayjs | null>(null);
  const [submittingInvoice, setSubmittingInvoice] = useState(false);
  // 回款相关状态
  const [receiptAmount, setReceiptAmount] = useState<string>('');
  const [receiptDate, setReceiptDate] = useState<dayjs.Dayjs | null>(null);
  const [submittingReceipt, setSubmittingReceipt] = useState(false);
  // 查询开票记录
  const {
    data: invoiceData,
    loading: invoiceLoading,
    refetch: refetchInvoices
  } = useQuery(GET_BILL_INVOICES_BY_BILL_ID, {
    variables: { billId: billId },
    fetchPolicy: 'network-only',
    skip: !billId
  });
  // 查询收款记录
  const {
    data: receiptData,
    loading: receiptLoading,
    refetch: refetchReceipts
  } = useQuery(GET_BILL_RECEIPTS_BY_BILL_ID, {
    variables: { billId: billId },
    fetchPolicy: 'network-only',
    skip: !billId
  })
  // 开票记录列表
  const invoiceRecords = invoiceData?.getBillInvoicesByBillId || [];

  // 计算开票总额
  const totalInvoicedAmount = invoiceRecords
      .filter((inv: any) => inv.status === 'SUCCESS')
      .reduce((sum: number, inv: any) => sum + (Number(inv.invoiceAmount) || 0), 0);
// 收款记录列表
  const receiptRecords = receiptData?.getBillReceiptsByBillId || [];

  // 计算收款总额
  const totalReceivedAmount = receiptRecords
      .filter((r: any) => r.status === 'SUCCESS')
      .reduce((sum: number, r: any) => sum + (Number(r.amount) || 0), 0);

  // 处理提交开票
  const handleSubmitInvoice = async () => {
    if (!invoiceAmount || !invoiceDate) {
      message.warning('请填写开票金额和开票日期');
      return;
    }

    const amount = Math.round(parseFloat(invoiceAmount) * 100);
    if (isNaN(amount) || amount <= 0) {
      message.warning('请输入有效的开票金额');
      return;
    }

    setSubmittingInvoice(true);
    try {
      const result = await createBillInvoice({
        variables: {
          input: {
            billId: billId,
            invoiceAmount: amount,
            currency: bill.currency,
            invoiceDate: parseInt(invoiceDate.format('YYYYMMDD')),
            invoicedAt: new Date().toISOString()
          }
        }
      });

      if (result.data?.createBillInvoice?.success) {
        message.success('开票记录创建成功');
        setInvoiceAmount('');
        setInvoiceDate(null);
        refetchInvoices();
      } else {
        message.error(result.data?.createBillInvoice?.errorMessage || '创建开票记录失败');
      }
    } catch (error) {
      console.error('创建开票记录失败:', error);
      message.error('创建开票记录失败: ' + (error as Error).message);
    } finally {
      setSubmittingInvoice(false);
    }
  };

  // 处理作废收款记录
  const handleRevokeReceipt = async (receiptId: string) => {
    Modal.confirm({
      title: '确认作废回款记录',
      content: '确定要作废这条回款记录吗？作废后不可恢复。',
      okText: '确认作废',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          const result = await revokeBillReceipt({
            variables: {
              id: receiptId,
              reason: '用户手动作废'
            }
          });

          if (result.data?.revokeBillReceipt?.success) {
            message.success('回款记录已作废');
            refetchReceipts();
          } else {
            message.error(result.data?.revokeBillReceipt?.errorMessage || '作废回款记录失败');
          }
        } catch (error) {
          console.error('作废回款记录失败:', error);
          message.error('作废回款记录失败: ' + (error as Error).message);
        }
      }
    });
  };

  // 处理提交收款
  const handleSubmitReceipt = async () => {
    if (!receiptAmount || !receiptDate) {
      message.warning('请填写回款金额和回款日期');
      return;
    }

    const amount = Math.round(parseFloat(receiptAmount) * 100);
    if (isNaN(amount) || amount <= 0) {
      message.warning('请输入有效的回款金额');
      return;
    }

    setSubmittingReceipt(true);
    try {
      const result = await createBillReceipt({
        variables: {
          input: {
            billId: billId,
            amount: amount,
            currency: bill.currency,
            receiptDate: parseInt(receiptDate.format('YYYYMMDD')),
            receivedAt: new Date().toISOString()
          }
        }
      });

      if (result.data?.createBillReceipt?.success) {
        message.success('回款记录创建成功');
        setReceiptAmount('');
        setReceiptDate(null);
        refetchReceipts();
      } else {
        message.error(result.data?.createBillReceipt?.errorMessage || '创建回款记录失败');
      }
    } catch (error) {
      console.error('创建回款记录失败:', error);
      message.error('创建回款记录失败: ' + (error as Error).message);
    } finally {
      setSubmittingReceipt(false);
    }
  };
  const billDetail: SettlementBillDetailResponseType | undefined = data?.getBillDetail;

  // 处理 Tab 切换
  const handleTabChange = (key: string) => {
    setActiveTabKey(key);
    if (key === 'billStatusTransition') {
      loadBillStatusTransitionRecords();
    }
    // 切换到导出记录时自动加载数据
    if (key === 'export') {
      loadExportHistory();
    }
    // 切换到汇总历史时自动加载数据
    if (key === 'summary') {
      loadSummaryHistory();
    }
    // 切换到发送记录时自动加载数据
    if (key === 'send') {
      loadSendHistory();
    }
  };

  const handleRefreshBillStatusTransition = () => {
    loadBillStatusTransitionRecords();
  };

  // 手动刷新导出记录
  const handleRefreshExportHistory = () => {
    loadExportHistory();
  };

  // 手动刷新汇总历史
  const handleRefreshSummaryHistory = () => {
    loadSummaryHistory();
  };

  // 手动刷新发送记录
  const handleRefreshSendHistory = () => {
    loadSendHistory();
  };

  // 查看汇总任务进度
  const handleViewSummaryProgress = (taskId: string) => {
    setSummaryTaskId(taskId);
    setSummaryProgressModalVisible(true);
  };

  // 取消汇总任务
  const handleCancelSummary = async (taskId: string) => {
    Modal.confirm({
      title: '确认取消汇总任务',
      content: '确定要取消这个汇总任务吗？取消后任务将停止执行。',
      okText: '确认取消',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          const result = await cancelBillSummary({
            variables: { taskId }
          });

          if (result.data?.cancelBillSummary?.success) {
            message.success('汇总任务已取消');
            // 刷新汇总历史和账单详情
            loadSummaryHistory();
            refetch();
          } else {
            message.error(result.data?.cancelBillSummary?.message || '取消汇总任务失败');
          }
        } catch (error) {
          console.error('取消汇总任务失败:', error);
          message.error('取消汇总任务失败: ' + (error as Error).message);
        }
      }
    });
  };

  // 汇总任务完成回调
  const handleSummaryCompleted = (success: boolean) => {
    if (success) {
      message.success('账单汇总完成！');
      // 刷新汇总历史和账单详情
      loadSummaryHistory();
      refetch();
    } else {
      message.error('账单汇总失败！');
    }
  };

  // 处理下载导出文件
  const handleDownloadExport = async (record: BillExportHistoryType) => {
    if (record.status !== 'SUCCESS') {
      message.warning('只能下载成功的导出文件');
      return;
    }

    if (!record.fileName) {
      message.error('未找到导出文件信息');
      return;
    }

    try {
      message.loading({ content: '正在获取下载链接...', key: 'download' });

      // 实时获取新的下载URL
      const result = await getExportFileDownloadUrl({
        variables: { taskId: record.taskId }
      });

      const downloadUrl = result.data?.getExportFileDownloadUrl;

      if (!downloadUrl) {
        message.error({ content: '获取下载链接失败', key: 'download' });
        return;
      }

      message.success({ content: '开始下载...', key: 'download', duration: 2 });
      downloadBillExportFile(downloadUrl, record.fileName);
    } catch (error) {
      console.error('获取下载链接失败:', error);
      message.error({ content: '获取下载链接失败: ' + (error as Error).message, key: 'download' });
    }
  };

  // 调试信息
  console.log('SettlementBillDetail Debug:', {
    billId,
    loading,
    error,
    data,
    billDetail
  });

  // 处理内部确认账单
  const handleConfirmBill = () => {
    let remark = '';
    Modal.confirm({
      title: '内部确认账单',
      content: (
        <div className="pb-6">
          <div className="mb-2">确认后账单将进入【待外部确认】，请填写备注：</div>
          <Input.TextArea
            maxLength={200}
            showCount
            rows={4}
            placeholder="请输入内部确认备注"
            onChange={(e) => {
              remark = e.target.value;
            }}
          />
        </div>
      ),
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          const result = await confirmBill({
            variables: { billId, remark: remark.trim() || '账单内部确认' }
          });
          if (result.data?.confirmBillInternal?.success) {
            message.success('内部确认成功');
            await refetch();
            loadBillStatusTransitionRecords();
            return;
          }
          message.error(result.data?.confirmBillInternal?.errorMessage || '内部确认失败');
        } catch (error) {
          message.error('内部确认失败: ' + (error as Error).message);
        }
      }
    });
  };

  const handleOnBehalfConfirmSubmit = async (payload: {
    voucherObjectKey: string;
    voucherFileName: string;
  }) => {
    setOnBehalfSubmitting(true);
    try {
      const result = await confirmBillOnBehalfExternal({
        variables: {
          billId,
          voucherObjectKey: payload.voucherObjectKey,
          voucherFileName: payload.voucherFileName,
        }
      });
      if (result.data?.confirmBillOnBehalfExternal?.success) {
        message.success('代外部确认成功');
        setOnBehalfConfirmVisible(false);
        await refetch();
        loadBillStatusTransitionRecords();
        return;
      }
      message.error(result.data?.confirmBillOnBehalfExternal?.errorMessage || '代外部确认失败');
    } catch (error) {
      message.error('代外部确认失败: ' + (error as Error).message);
    } finally {
      setOnBehalfSubmitting(false);
    }
  };

  const handleOnBehalfRejectSubmit = async (remark: string) => {
    setOnBehalfSubmitting(true);
    try {
      const result = await rejectBillOnBehalfExternal({
        variables: { billId, remark }
      });
      if (result.data?.rejectBillOnBehalfExternal?.success) {
        message.success('代外部驳回成功');
        setOnBehalfRejectVisible(false);
        await refetch();
        loadBillStatusTransitionRecords();
        return;
      }
      message.error(result.data?.rejectBillOnBehalfExternal?.errorMessage || '代外部驳回失败');
    } catch (error) {
      message.error('代外部驳回失败: ' + (error as Error).message);
    } finally {
      setOnBehalfSubmitting(false);
    }
  };

  // 处理发送账单预览（Step1）
  const handleOpenBillSendModal = async () => {
    try {
      const result = await loadBillSendPreview({
        variables: { billId }
      });
      const preview = result.data?.previewBillSend ?? null;

      if (!preview) {
        message.error('获取发送预览失败');
        return;
      }

      if (!preview.canSend) {
        message.error(preview.blockReasonMessage || '当前账单不可发送');
        return;
      }

      setBillSendPreview(preview);
      setBillSendModalVisible(true);
    } catch (e) {
      message.error('获取发送预览失败: ' + (e as Error).message);
    }
  };

  // 提交发送（Step2）
  const handleConfirmSendBill = async () => {
    if (!billSendPreview) return;
    setBillSendSubmitting(true);

    try {
      const result = await sendBillMutation({
        variables: {
          sendRequest: { billId }
        }
      });

      const res = result.data?.sendBill;
      if (res?.success) {
        message.success(`发送完成：成功 ${res.successCount} 封，失败 ${res.failureCount} 封`);
        setBillSendModalVisible(false);
        setBillSendPreview(null);
        await refetch();
        return;
      }

      message.error(res?.errorMessage || '发送失败');
    } catch (e) {
      message.error('发送失败: ' + (e as Error).message);
    } finally {
      setBillSendSubmitting(false);
    }
  };

  // 处理导出账单
  const handleExportBill = () => {
    setExportModalVisible(true);
  };

  // 执行导出（带确认）
  const handleExport = async (template: BillExportTemplateType, language: ExportLanguage) => {
    if (!billDetail?.bill) return;

    const bill = billDetail.bill;

    try {
      // 先检查是否有导出历史
      message.loading({ content: '正在检查导出历史...', key: 'check', duration: 0 });
      const { data: historyData } = await loadExportHistory();
      message.destroy('check');

      const historyRecords = historyData?.getExportHistory?.content || [];
      const successRecords = historyRecords.filter((r: any) => r.status === 'SUCCESS');

      // 如果有成功的导出记录，显示确认对话框
      if (successRecords.length > 0) {
        const latestRecord = successRecords[0];
        const confirmResult = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: '确认导出账单',
            content: (
              <div>
                <p>该账单已有导出记录，最近一次导出：</p>
                <ul style={{ marginTop: 8, marginLeft: 20 }}>
                  <li>导出时间: {new Date(latestRecord.createdAt).toLocaleString('zh-CN')}</li>
                  <li>文件名: {latestRecord.fileName}</li>
                  <li>模板类型: {latestRecord.templateType}</li>
                </ul>
                <p style={{ marginTop: 12, color: '#faad14' }}>
                  <strong>提示：</strong>重新导出将生成新的文件，之前的导出记录仍会保留。
                </p>
                <p>确定要继续导出吗？</p>
              </div>
            ),
            okText: '确定导出',
            cancelText: '取消',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
            width: 520
          });
        });

        if (!confirmResult) {
          return;
        }
      } else {
        // 没有历史记录，也要确认一下
        const confirmResult = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: '确认导出账单',
            content: (
              <div>
                <p>将为以下账单创建导出任务：</p>
                <ul style={{ marginTop: 8, marginLeft: 20 }}>
                  <li>账单ID: {bill.id}</li>
                  <li>客户: {bill.clientName}</li>
                  <li>账单类型: {getBillTypeLabel(bill.billType)}</li>
                  <li>账单金额: {formatSettlementAmountFromMajor(bill.billAmount, bill.currency)}</li>
                </ul>
                <p style={{ marginTop: 12 }}>确定要导出吗？</p>
              </div>
            ),
            okText: '确定导出',
            cancelText: '取消',
            onOk: () => resolve(true),
            onCancel: () => resolve(false)
          });
        });

        if (!confirmResult) {
          return;
        }
      }

      // 执行导出
      const result = await exportBill({
        variables: {
          exportRequest: {
            billId: bill.id,
            template,
            format: 'EXCEL',
            language
          }
        }
      });

      if (result.data?.exportBill?.success) {
        const taskIds = result.data.exportBill.taskIds || [];
        message.success(`导出任务已创建（${taskIds.length}个文件）`);
        setExportModalVisible(false);
        // 显示进度弹窗（传入所有任务ID）
        setExportTaskId(taskIds.length > 0 ? taskIds.join(',') : '');
        setProgressModalVisible(true);
      } else {
        message.error(result.data?.exportBill?.errorMessage || '导出失败');
      }
    } catch (error) {
      message.destroy('check');
      message.error('导出失败: ' + (error as Error).message);
    }
  };

  // 处理导出完成
  const handleExportCompleted = (task: any) => {
    if (task.status === 'SUCCESS') {
      message.success('导出成功！');
    } else if (task.status === 'FAILED') {
      message.error(`导出失败: ${task.errorMessage || '未知错误'}`);
    }
  };

  // 费用汇总表格列定义
  const feeSummaryColumns = [
    {
      title: '费用项名称',
      dataIndex: 'feeItemName',
      key: 'feeItemName',
      width: 220,
      ellipsis: true,
      render: (name: string) => <span title={name}>{name}</span>
    },
    {
      title: '汇总金额',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 140,
      render: (amount: string, record: BillFeeSummaryType) => (
        <span style={{ color: record.amountSign === -1 ? '#ff4d4f' : '#52c41a' }}>
          {`${record.amountSign === -1 ? '-' : '+'}${amount}`}
        </span>
      ),
    },
    {
      title: '是否可调账',
      dataIndex: 'allowAdjustment',
      key: 'allowAdjustment',
      width: 150,
      render: (allow: boolean) => {
        if (bill.isCanceled) {
          return (
            <Tag color="red">
              账单已作废不可调账
            </Tag>
          );
        }
        return (
          <Tag color={allow ? 'green' : 'red'}>
            {allow ? '可调账' : '不可调账'}
          </Tag>
        );
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      render: (record: BillFeeSummaryType) => (
        <AdjustmentForm
          billId={billId!}
          feeItemId={record.feeItemId}
          feeItemName={record.feeItemName}
          currentAmount={Math.round(parseFloat(String(record.totalAmount).replace(/[^\d.-]/g, '')) * 100)}
          allowAdjustment={record.allowAdjustment}
          currency={bill.currency}
          isBillCanceled={bill.isCanceled}
          onSuccess={() => refetch()}
        />
      ),
    },
  ];

  // 调账记录表格列定义
  const adjustmentColumns = [
    {
      title: '调账任务ID',
      dataIndex: 'taskId',
      key: 'taskId',
    },
    {
      title: '调账类型',
      dataIndex: 'adjustmentType',
      key: 'adjustmentType',
    },
    {
      title: '调账金额',
      dataIndex: 'adjustmentAmount',
      key: 'adjustmentAmount',
      render: (amount: string) => `${amount}`,
    },
    {
      title: '调账原因',
      dataIndex: 'reason',
      key: 'reason',
    },
    {
      title: '提交时间',
      dataIndex: 'submittedAt',
      key: 'submittedAt',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusConfig = {
          'INITIATED': { color: 'orange', text: '调账发起' },
          'COMPLETED': { color: 'green', text: '调账完成' },
        };
        const config = statusConfig[status as keyof typeof statusConfig];
        return <Tag color={config?.color}>{config?.text}</Tag>;
      },
    },
  ];

  // 汇总历史表格列定义
  const summaryHistoryColumns = [
    {
      title: '任务ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: '操作人',
      dataIndex: 'operatorName',
      key: 'operatorName',
      width: 120,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: number) => {
        const statusConfig: Record<number, { color: string; text: string }> = {
          0: { color: 'default', text: '待处理' },
          1: { color: 'processing', text: '处理中' },
          2: { color: 'success', text: '成功' },
          3: { color: 'error', text: '失败' },
          4: { color: 'warning', text: '已取消' },
        };
        const config = statusConfig[status] || { color: 'default', text: '未知' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '进度',
      dataIndex: 'progress',
      key: 'progress',
      width: 150,
      render: (progress: number, record: BillSummaryHistory) => {
        const status = record.status;
        const getStatus = () => {
          if (status === 1) return 'active';
          if (status === 2) return 'success';
          if (status === 3 || status === 4) return 'exception';
          return 'normal';
        };
        return (
          <Progress
            percent={progress}
            status={getStatus()}
            format={(percent) => `${percent}%`}
            size="small"
          />
        );
      },
    },
    {
      title: '当前步骤',
      dataIndex: 'currentStep',
      key: 'currentStep',
      width: 200,
      ellipsis: true,
      render: (step: string | undefined) => step || '-',
    },
    {
      title: '汇总费用项数',
      dataIndex: 'summaryCount',
      key: 'summaryCount',
      width: 120,
      render: (count: number | undefined) => count ?? '-',
    },
    {
      title: '汇总金额',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 150,
      render: (amount: number | undefined) => formatSettlementAmountFromCents(amount, bill.currency),
    },
    {
      title: '创建时间 (本地时区)',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: (time: string) => formatTime(time),
    },
    {
      title: '完成时间 (本地时区)',
      dataIndex: 'completedAt',
      key: 'completedAt',
      width: 170,
      render: (time: string | undefined) => formatTime(time),
    },
    {
      title: '错误信息',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      width: 180,
      ellipsis: true,
      render: (msg: string | undefined) => {
        if (!msg) return '-';
        return <span style={{ color: '#ff4d4f' }} title={msg}>{msg}</span>;
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: BillSummaryHistory) => {
        // 运行中的任务可以查看进度和取消
        if (record.status === 1) {
          return (
            <Space>
              <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_CHECK]}>
                <Button
                  type="link"
                  size="small"
                  onClick={() => handleViewSummaryProgress(record.id)}
                >
                  查看进度
                </Button>
              </HasResource>
              <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_OPERATE]}>
                <Button
                  type="link"
                  size="small"
                  danger
                  onClick={() => handleCancelSummary(record.id)}
                >
                  取消
                </Button>
              </HasResource>
            </Space>
          );
        }
        // 待处理的任务可以取消
        if (record.status === 0) {
          return (
            <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_OPERATE]}>
              <Button
                type="link"
                size="small"
                danger
                onClick={() => handleCancelSummary(record.id)}
              >
                取消
              </Button>
            </HasResource>
          );
        }
        return '-';
      },
    },
  ];

  // 导出记录表格列定义
  const exportHistoryColumns = [
    {
      title: '任务ID',
      dataIndex: 'taskId',
      key: 'taskId',
      width: 80,
    },
    {
      title: '文件类型',
      dataIndex: 'fileType',
      key: 'fileType',
      width: 100,
      render: (fileType: number) => {
        const color = getFileTypeColor(fileType as 0 | 1 | 2);
        return <Tag color={color}>{formatFileType(fileType as 0 | 1 | 2)}</Tag>;
      },
    },
    {
      title: '文件名',
      dataIndex: 'fileName',
      key: 'fileName',
      width: 250,
      ellipsis: true,
    },
    {
      title: '文件大小',
      dataIndex: 'fileSize',
      key: 'fileSize',
      width: 100,
      render: (size: number | undefined) => size ? formatFileSize(size) : '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const config = formatExportStatus(status);
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '进度',
      dataIndex: 'progress',
      key: 'progress',
      width: 150,
      render: (progress: number, record: BillExportHistoryType) => {
        const status = record.status;
        const getStatus = () => {
          if (status === 'RUNNING') return 'active';
          if (status === 'SUCCESS') return 'success';
          if (status === 'FAILED') return 'exception';
          return 'normal';
        };
        return (
          <Progress
            percent={progress}
            status={getStatus()}
            format={(percent) => `${percent}%`}
            size="small"
          />
        );
      },
    },
    {
      title: '当前步骤',
      dataIndex: 'currentStep',
      key: 'currentStep',
      width: 200,
      ellipsis: true,
      render: (step: string | undefined) => step || '-',
    },
    {
      title: '创建时间 (本地时区)',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: (time: string) => formatTime(time),
    },
    {
      title: '完成时间 (本地时区)',
      dataIndex: 'completedAt',
      key: 'completedAt',
      width: 170,
      render: (time: string | undefined) => formatTime(time),
    },
    {
      title: '操作',
      key: 'actions',
      width: 80,
      fixed: 'right' as const,
      render: (_: any, record: BillExportHistoryType) => (
          <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_OPERATE]}>
            <Button
                type="link"
                size="small"
                icon={<DownloadOutlined />}
                disabled={record.status !== 'SUCCESS'}
                onClick={() => handleDownloadExport(record)}
            >
              下载
            </Button>
          </HasResource>
      ),
    },
  ];

  // 发送记录表格列定义
  const sendHistoryColumns = [
    {
      title: '日期时间',
      key: 'sentAt',
      width: 180,
      render: (_: any, record: BillSendRecordType) =>
        record.sentAt ? new Date(record.sentAt).toLocaleString('zh-CN') : '-',
    },
    {
      title: '发送邮箱',
      dataIndex: 'senderEmail',
      key: 'senderEmail',
      width: 220,
      ellipsis: true,
    },
    {
      title: '收件邮箱',
      dataIndex: 'receiverEmail',
      key: 'receiverEmail',
      width: 220,
      ellipsis: true,
    },
    {
      title: '发送文件地址',
      dataIndex: 'attachmentUrl',
      key: 'attachmentUrl',
      width: 320,
      ellipsis: true,
      render: (url: string | null | undefined) => {
        if (!url) return '-';
        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
            title={url}
          >
            {url}
          </a>
        );
      },
    },
    {
      title: '发送人',
      dataIndex: 'operatorName',
      key: 'operatorName',
      width: 150,
      ellipsis: true,
      render: (name: string | undefined) => name || '-',
    },
  ];

  if (loading) {
    return <div>加载中...</div>;
  }

  if (!billDetail) {
    return <div>账单不存在</div>;
  }

  const { bill, feeSummaries, adjustmentRecords } = billDetail;
  const showFinanceRecords = !bill.isCanceled && bill.billType !== BillTypeEnum.SLA_BILL;
  const exportHistory = exportHistoryData?.getExportHistory?.content || [];
  const summaryHistory = summaryHistoryData?.getBillSummaryHistory || [];
  const sendHistory = sendHistoryData?.getBillSendHistory?.content || [];
  const billStatusTransitionRecords: BillStatusTransitionRecord[] =
    billStatusTransitionData?.getBillStatusTransitionRecords || [];

  const canInternalConfirm =
    !bill.isCanceled &&
    (bill.status === 'INTERNAL_ALREADY' ||
      bill.status === 'ADJUSTMENT_PENDING' ||
      bill.status === 'EXTERNAL_REJECTED');
  const canOnBehalfExternal =
    !bill.isCanceled && bill.status === 'INTERNAL_CONFIRMED';

  // 获取浏览器本地时区
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // 创建时区格式化函数，使用浏览器的本地时区（与列标题一致）
  const formatTime = (timeString: string | undefined | null) => {
    if (!timeString) return '-';
    return formatTimeWithTimezone(timeString, browserTimeZone);
  };

  const billStatusTransitionColumns = [
    {
      title: '操作时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (time: string) => formatTime(time)
    },
    {
      title: '状态流转',
      key: 'statusTransition',
      width: 280,
      render: (_: unknown, record: BillStatusTransitionRecord) => (
        <span>
          {record.fromStatusLabel || '-'} {'->'} {record.toStatusLabel}
        </span>
      )
    },
    {
      title: '操作人',
      key: 'operator',
      width: 160,
      render: (_: unknown, record: BillStatusTransitionRecord) =>
        record.operatorName || record.operatorId || '-'
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      width: 240,
      ellipsis: true,
      render: (remark: string | null | undefined) => (
        <span title={remark || undefined}>{remark || '-'}</span>
      )
    },
    {
      title: '凭证',
      key: 'attachment',
      width: 120,
      render: (_: unknown, record: BillStatusTransitionRecord) =>
        record.attachmentDownloadUrl ? (
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => window.open(record.attachmentDownloadUrl!, '_blank', 'noreferrer')}
          >
            下载凭证
          </Button>
        ) : (
          '-'
        )
    }
  ];


  return (
    <div className="p-6">
      {/* 页面头部 */}
      <div className="mb-4">
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/settlement/bill-list')}
          className="mb-2"
        >
          返回
        </Button>

        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">账单详情</h1>

          <Space>
            <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_OPERATE]}>
                {canInternalConfirm && (
                    <Button
                        type="primary"
                        icon={<CheckOutlined />}
                        onClick={handleConfirmBill}
                    >
                        内部确认账单
                    </Button>
                )}
            </HasResource>

            <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_OPERATE]}>
                {canOnBehalfExternal && (
                    <Button onClick={() => setOnBehalfConfirmVisible(true)}>
                        代外部确认账单
                    </Button>
                )}
            </HasResource>

            <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_OPERATE]}>
                {canOnBehalfExternal && (
                    <Button danger onClick={() => setOnBehalfRejectVisible(true)}>
                        代外部驳回账单
                    </Button>
                )}
            </HasResource>

            <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_OPERATE]}>
              {canSendBill(bill) && (
                <Button
                  icon={<MailOutlined />}
                  onClick={handleOpenBillSendModal}
                  title={getSendBillButtonTitle(bill)}
                >
                  {t('settlementBillSend.actions.sendBill')}
                </Button>
              )}
            </HasResource>

            <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_OPERATE]}>
              <Button
                icon={<ExportOutlined />}
                onClick={handleExportBill}
                disabled={!canExportBill(bill)}
                title={getExportButtonTitle(bill)}
              >
                导出账单
              </Button>
            </HasResource>

            {canViewInvoice(bill) && (
              <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_CHECK]}>
                <Button onClick={() => navigate(`/settlement/bill-detail/${billId}/invoice`)}>
                  Invoice
                </Button>
              </HasResource>
            )}
          </Space>
        </div>
      </div>

      {/* 账期信息 */}
      <BillingPeriodRuleCard
        billingPeriodInstanceId={billDetail?.bill?.billingPeriodInstanceId}
      />

      {/* 账单基本信息 */}
      <Card title="账单信息" className="mb-4">
        <Descriptions column={2}>
          <Descriptions.Item label="账单ID">{bill.id}</Descriptions.Item>
          <Descriptions.Item label="客户名称">{bill.clientName}</Descriptions.Item>
          <Descriptions.Item label="结算客户ID">{bill.clientId}</Descriptions.Item>
          <Descriptions.Item label="客户ID">{bill.channelId}</Descriptions.Item>
          <Descriptions.Item label="账单类型">
            {renderBillTypeTag(bill.billType)}
          </Descriptions.Item>
          <Descriptions.Item label="账单状态">
            {bill.isCanceled ? (
              <Tag color={getBillStatusColor('CANCELED')}>{getBillStatusLabel('CANCELED')}</Tag>
            ) : (
              <Tag color={getBillStatusColor(bill.status)}>{getBillStatusLabel(bill.status)}</Tag>
            )}
          </Descriptions.Item>

          <Descriptions.Item label={t('settlementBillSend.labels.sendStatus')}>
            {bill.isSent ? (
              <Tag color="green">{t('settlementBillSend.status.sent')}</Tag>
            ) : (
              <Tag color="default">{t('settlementBillSend.status.notSent')}</Tag>
            )}
          </Descriptions.Item>

          <Descriptions.Item label={t('settlementBillSend.labels.lastSentAt')}>
            {bill.lastSentAt ? formatTime(bill.lastSentAt) : '-'}
          </Descriptions.Item>

          <Descriptions.Item label="账单金额">
            <span className="text-lg font-bold text-red-600">{formatSettlementAmountFromMajor(bill.billAmount, bill.currency)}</span>
          </Descriptions.Item>
          {bill.orderCount != null ? (
            <Descriptions.Item label="订单数量">{Number(bill.orderCount)}</Descriptions.Item>
          ) : null}
          <Descriptions.Item label={`账单周期 (${bill.timeZone})`}>
            {formatTimeWithTimezone(bill.periodStart, bill.timeZone)} ~ {formatTimeWithTimezone(bill.periodEnd, bill.timeZone)}
          </Descriptions.Item>
          <Descriptions.Item label="账单时区">{bill.timeZone}</Descriptions.Item>
          <Descriptions.Item label={`创建时间 (本地时区)`}>
            {bill.createdAt ? new Date(bill.createdAt).toLocaleString('zh-CN', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={`更新时间 (本地时区)`}>
            {bill.updatedAt ? new Date(bill.updatedAt).toLocaleString('zh-CN', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }) : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 费用汇总 */}
      <Card
        title="费用汇总"
        className="mb-4"
        extra={
          canAdjustBill(bill) ? (
            <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_OPERATE]}>
              <Button type="primary" onClick={() => setDetailAdjustmentModalVisible(true)}>
                明细调账
              </Button>
            </HasResource>
          ) : null
        }
      >
        <div className="max-w-full overflow-hidden">
          <Table
            columns={feeSummaryColumns}
            dataSource={feeSummaries}
            rowKey="feeItemId"
            pagination={false}
            size="small"
            className="mb-4"
            scroll={{ x: 630 }}
            tableLayout="fixed"
          />
        </div>
      </Card>
      {showFinanceRecords && <>
      {/* 开票进度 */}
      <Card
          title={
            <Space>
              <span>开票进度</span>
              <span className="text-red-600 font-bold">总额：<span className="ml-2">{formatSettlementAmountFromCents(totalInvoicedAmount, bill.currency)}（不含作废金额）</span></span>
            </Space>
          }
          className="mb-4"
      >
        {/* 开票记录列表 */}
        <div className="mb-4 overflow-x-auto">
          {invoiceLoading ? (
              <div className="text-center py-4">加载中...</div>
          ) : invoiceRecords.length === 0 ? (
              <Empty description="暂无开票记录" />
          ) : (
              <div className="min-w-[700px]">
                <div className="grid grid-cols-[180px_130px_220px_240px_90px_36px] text-xs pb-2 border-b gap-2">
                  <div>操作时间</div>
                  <div>开票日期</div>
                  <div className="text-center">开票金额</div>
                  <div>操作人</div>
                  <div>状态</div>
                  <div></div>
                </div>
                {invoiceRecords.map((invoice: any) => (
                    <div key={invoice.id} className="grid grid-cols-[180px_130px_220px_240px_90px_36px] text-sm py-2 border-b border-dashed last:border-b-0 gap-2 items-center">
                      <div className="truncate">{invoice.invoicedAt ? formatTime(invoice.invoicedAt) : '-'}</div>
                      <div className="truncate">{invoice.invoiceDate ? String(invoice.invoiceDate).replace(/(\d{4})(\d{2})(\d{2})/, '$1.$2.$3') : '-'}</div>
                      <div className="text-center truncate">{formatSettlementAmountFromCents(invoice.invoiceAmount, invoice.currency)}</div>
                      <div className="truncate">{invoice.operatorName || '-'}</div>
                      <div>
                        <Tag color={
                          invoice.status === 'SUCCESS' ? 'green' :
                              invoice.status === 'FAILED' ? 'red' :
                                  invoice.status === 'CANCELLED' ? 'gray' : 'default'
                        } style={{ margin: 0 }}>
                          {invoice.status === 'SUCCESS' ? '开票成功' :
                              invoice.status === 'FAILED' ? '开票失败' :
                                  invoice.status === 'CANCELLED' ? '已作废' : '发起成功'}
                        </Tag>
                      </div>
                    </div>
                ))}
              </div>
          )}
        </div>

        <Divider />

        {/* 提交开票表单 */}
        {/* <div>
          <div className="flex gap-4 items-end">
            <div className="w-48">
              <Input
                  size="middle"
                  placeholder={`开票金额（${bill.currency}）`}
                  value={invoiceAmount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*\.?\d{0,2}$/.test(value)) {
                      setInvoiceAmount(value);
                    }
                  }}
              />
            </div>
            <div className="w-48">
              <DatePicker
                  size="middle"
                  style={{ width: '100%' }}
                  value={invoiceDate}
                  onChange={setInvoiceDate}
                  placeholder="开票日期"
                  format="YYYY-MM-DD"
              />
            </div>
            <Button
                type="primary"
                onClick={handleSubmitInvoice}
                loading={submittingInvoice}
                disabled={!invoiceAmount || !invoiceDate}
            >
              提交记录
            </Button>
          </div>
        </div> */}
      </Card>

    {/* 回款进度 */}
    <Card
        title={
          <Space>
            <span>回款进度</span>
            <span className="text-red-600 font-bold">总额：<span className="ml-2">{formatSettlementAmountFromCents(totalReceivedAmount, bill.currency)}（不含作废金额）</span></span>
          </Space>
        }
        className="mb-4"
    >
      {/* 收款记录列表 */}
      <div className="mb-4 overflow-x-auto">
        {receiptLoading ? (
            <div className="text-center py-4">加载中...</div>
        ) : receiptRecords.length === 0 ? (
            <Empty description="暂无回款记录" />
        ) : (
            <div className="min-w-[700px]">
              <div className="grid grid-cols-[180px_130px_220px_240px_90px_36px] text-xs pb-2 border-b gap-2">
                <div>操作时间</div>
                <div>回款日期</div>
                  <div className="text-center">回款金额</div>
                <div>操作人</div>
                <div>状态</div>
                <div></div>
              </div>
              {receiptRecords.map((receipt: any) => (
                  <div key={receipt.id} className="grid grid-cols-[180px_130px_220px_240px_90px_36px] text-sm py-2 border-b border-dashed last:border-b-0 gap-2 items-center">
                    <div className="truncate">{receipt.createdAt ? formatTime(receipt.createdAt) : '-'}</div>
                    <div className="truncate">{receipt.receiptDate ? String(receipt.receiptDate).replace(/(\d{4})(\d{2})(\d{2})/, '$1.$2.$3') : '-'}</div>
                    <div className="text-center truncate">{formatSettlementAmountFromCents(receipt.amount, receipt.currency)}</div>
                    <div className="truncate">{receipt.operatorName || '-'}</div>
                    <div>
                      <Tag color={
                        receipt.status === 'SUCCESS' ? 'green' :
                            receipt.status === 'FAILED' ? 'red' :
                                receipt.status === 'REVOKED' ? 'gray' : 'default'
                      } style={{ margin: 0 }}>
                        {receipt.status === 'SUCCESS' ? '回款成功' :
                            receipt.status === 'FAILED' ? '回款失败' :
                                receipt.status === 'REVOKED' ? '已作废' : '发起成功'}
                      </Tag>
                    </div>
                    <div>
                      {receipt.status !== 'REVOKED' && (
                          <Button type="link" danger size="small" icon={<CloseCircleOutlined />} onClick={() => handleRevokeReceipt(receipt.id)} />
                      )}
                    </div>
                  </div>
              ))}
            </div>
        )}
      </div>

      <Divider />

      {/* 提交回款表单 */}
      <div>
        <div className="flex gap-4 items-end">
          <div className="w-48">
            <Input
                size="middle"
                  placeholder={`回款金额（${bill.currency}）`}
                value={receiptAmount}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*\.?\d{0,2}$/.test(value)) {
                    setReceiptAmount(value);
                  }
                }}
            />
          </div>
          <div className="w-48">
            <DatePicker
                size="middle"
                style={{ width: '100%' }}
                value={receiptDate}
                onChange={setReceiptDate}
                placeholder="回款日期"
                format="YYYY-MM-DD"
            />
          </div>
          <Button
              type="primary"
              onClick={handleSubmitReceipt}
              loading={submittingReceipt}
              disabled={!receiptAmount || !receiptDate}
          >
            提交记录
          </Button>
        </div>
      </div>
    </Card>
      </>}
      {/* 调账记录和导出记录 */}
      <Card className="mb-4">
        <Tabs 
          activeKey={activeTabKey} 
          onChange={handleTabChange}
          items={[
            {
              key: 'billStatusTransition',
              label: '账单状态流转记录',
              children: (
                <div className="max-w-full overflow-hidden">
                  <div className="mb-4 flex justify-end">
                    <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_CHECK]}>
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={handleRefreshBillStatusTransition}
                        loading={billStatusTransitionLoading}
                      >
                        刷新
                      </Button>
                    </HasResource>
                  </div>
                  <Table
                    columns={billStatusTransitionColumns}
                    dataSource={billStatusTransitionRecords}
                    rowKey="id"
                    loading={billStatusTransitionLoading}
                    pagination={{
                      showSizeChanger: true,
                      showTotal: (total) => t('table.pagination.totalItems', { total }),
                      defaultPageSize: 10,
                      pageSizeOptions: ['10', '20', '50']
                    }}
                    size="small"
                    scroll={{ x: 960 }}
                    tableLayout="fixed"
                  />
                </div>
              )
            },
            {
              key: 'adjustment',
              label: '调账记录',
              children: (
                <AdjustmentRecordList 
                  billId={billId!} 
                  currency={bill.currency}
                  onRefresh={() => refetch()} 
                />
              )
            },
            {
              key: 'summary',
              label: '汇总历史',
              children: (
                <div className="max-w-full overflow-hidden">
                  <div className="mb-4 flex justify-end">
                    <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_CHECK]}>
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={handleRefreshSummaryHistory}
                        loading={summaryHistoryLoading}
                      >
                        刷新
                      </Button>
                    </HasResource>
                  </div>
                  <Table
                    columns={summaryHistoryColumns}
                    dataSource={summaryHistory}
                    rowKey="id"
                    loading={summaryHistoryLoading}
                    pagination={{
                      showSizeChanger: true,
                      showTotal: (total) => t('table.pagination.totalItems', { total }),
                      defaultPageSize: 10,
                      pageSizeOptions: ['10', '20', '50']
                    }}
                    size="small"
                    scroll={{ x: 1460 }}
                    tableLayout="fixed"
                  />
                </div>
              )
            },
            {
              key: 'export',
              label: '导出记录',
              children: (
                <div className="max-w-full overflow-hidden">
                  <div className="mb-4 flex justify-end">
                    <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_CHECK]}>
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={handleRefreshExportHistory}
                        loading={exportHistoryLoading}
                      >
                        刷新
                      </Button>
                    </HasResource>
                  </div>
                  <Table
                    columns={exportHistoryColumns}
                    dataSource={exportHistory}
                    rowKey="taskId"
                    loading={exportHistoryLoading}
                    pagination={{
                      showSizeChanger: true,
                      showTotal: (total) => t('table.pagination.totalItems', { total }),
                      defaultPageSize: 10,
                      pageSizeOptions: ['10', '20', '50']
                    }}
                    size="small"
                    scroll={{ x: 1400 }}
                    tableLayout="fixed"
                  />
                </div>
              )
            },
            {
              key: 'send',
              label: '发送记录',
              children: (
                <div className="max-w-full overflow-hidden">
                  <div className="mb-4 flex justify-end">
                    <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_CHECK]}>
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={handleRefreshSendHistory}
                        loading={sendHistoryLoading}
                      >
                        刷新
                      </Button>
                    </HasResource>
                  </div>
                  <Table
                    columns={sendHistoryColumns}
                    dataSource={sendHistory}
                    rowKey="id"
                    loading={sendHistoryLoading}
                    pagination={{
                      showSizeChanger: true,
                      showTotal: (total) => t('table.pagination.totalItems', { total }),
                      defaultPageSize: 10,
                      pageSizeOptions: ['10', '20', '50']
                    }}
                    size="small"
                    scroll={{ x: 1090 }}
                    tableLayout="fixed"
                  />
                </div>
              )
            },
            {
              key: 'invoiceSend',
              label: 'Invoice 发送记录',
              children: (
                <InvoiceSendRecordsTab billId={billId!} />
              )
            }
          ]}
        />
      </Card>

      {/* 导出模态框 */}
      <BillExportModal
        visible={exportModalVisible}
        bill={bill}
        onCancel={() => setExportModalVisible(false)}
        onExport={handleExport}
      />
      <BillExportProgressModal
        taskId={exportTaskId}
        visible={progressModalVisible}
        onClose={() => {
          setProgressModalVisible(false);
          setExportTaskId(null);
        }}
        onCompleted={handleExportCompleted}
      />

      {/* 发送账单弹窗 */}
      <BillSendModal
        visible={billSendModalVisible}
        billId={bill.id}
        preview={billSendPreview}
        onCancel={() => {
          setBillSendModalVisible(false);
          setBillSendPreview(null);
        }}
        onConfirmSend={handleConfirmSendBill}
        sendLoading={billSendSubmitting}
      />

      <OnBehalfExternalConfirmModal
        open={onBehalfConfirmVisible}
        submitting={onBehalfSubmitting}
        onCancel={() => setOnBehalfConfirmVisible(false)}
        onSubmit={handleOnBehalfConfirmSubmit}
      />
      <OnBehalfExternalRejectModal
        open={onBehalfRejectVisible}
        submitting={onBehalfSubmitting}
        onCancel={() => setOnBehalfRejectVisible(false)}
        onSubmit={handleOnBehalfRejectSubmit}
      />

      {/* 明细调账弹窗 */}
            <ClientDetailAdjustmentModal
              visible={detailAdjustmentModalVisible}
              onCancel={() => {
                setDetailAdjustmentModalVisible(false);
                void refetch();
              }}
              billId={bill.id}
              billInfo={{
                clientName: bill.clientName,
                billAmount: typeof bill.billAmount === 'string' ? bill.billAmount : String(bill.billAmount),
              }}
              onSuccess={() => refetch()}
            />

      {/* 汇总进度弹窗 */}
      {summaryTaskId && (
        <BillSummaryProgressModal
          visible={summaryProgressModalVisible}
          taskId={summaryTaskId}
          onClose={() => {
            setSummaryProgressModalVisible(false);
            setSummaryTaskId(null);
          }}
          onCompleted={handleSummaryCompleted}
        />
      )}

    </div>
  );
};

export default SettlementBillDetail;

